import React from 'react';

import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { Link } from '@techstyle/next-routes';
import { FormattedMessage } from '@techstyle/react-intl';

import { getCreditsApplied, getNextMemberCredit } from '../../utils/selectors';
import { Component as Price } from '../ExtendedPrice';
import Loading from '../Loading/Loading';
import { useIsShoppingBagRevampUX } from '../MemberCreditsCartToggle/MemberCreditsCartToggle';
import { withShoppingBagRevampUX } from '../MemberCreditsCartToggle/WithShoppingBagRevampUX';
import PromoConflictNMP from '../PromoConflictNMP/PromoConflictNMP';

import { CreditStatus, useGetCartTotalWithoutConflictingPromos } from './utils';

const statusAutotags = {
  applied: 'credit_applied',
  applying: 'credit_applied',
  available: 'apply_credit',
  no_credits_remaining: 'no_credits_remaining',
  removing: 'apply_credit',
  unavailable: 'no_credits',
};

const TextStyled = styled.span`
  font-size: 14px;
  font-weight: 400;
  max-width: 220px;
  ${({ theme }) => theme.subHeader.variants.subHeading4Uppercase.textStyles}
  color: ${props =>
    props.creditStatus === CreditStatus.applied ||
    props.creditStatus === CreditStatus.applying
      ? props.theme.colors.black
      : props.theme.colors.black900};

  ${({ theme, isShoppingBagRevampUX }) =>
    isShoppingBagRevampUX &&
    css`
      ${theme.subHeader.variants.subHeading4Uppercase.textStyles};
      max-width: 100%;
    `}
`;
const Text = withShoppingBagRevampUX(TextStyled);

export const MemberCreditsContainer = styled.div`
  font-weight: 300;
`;

export const PromoConflictHeader = styled.span`
  color: ${props => props.theme.colors.black900};
  font-size: 13px;
  font-weight: 400;
`;

const StyledCreditsCopyContainer = styled.div`
  padding: 0px 12px 8px;
  margin-top: 8px;
  ${({ isShoppingBagRevampUX }) =>
    isShoppingBagRevampUX &&
    css`
      margin: 0;
      padding: 12px 0;
    `}
`;

const CreditsCopyContainer = withShoppingBagRevampUX(
  StyledCreditsCopyContainer
);

const StyledKeepShoppingCTA = styled.div`
  border-color: #000000;
  border-width: 1px;
  border-style: solid;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin: 8px 12px;
  padding: 4px 0px;
  cursor: pointer;
  ${({ isShoppingBagRevampUX, theme }) =>
    isShoppingBagRevampUX &&
    css`
      background-color: ${theme.colors.white};
      border-color: ${theme.colors.gunmetal};
      min-height: 32px;
      padding: 0;
      margin: 0;
    `}
`;
const StyledKeepShoppingText = styled.span`
  ${({ isShoppingBagRevampUX, theme }) =>
    isShoppingBagRevampUX &&
    css`
      ${theme.subHeader.variants.subHeading4Uppercase.textStyles};
      color: ${theme.colors.gunmetal};
    `}
`;
const KeepShoppingCTA = withShoppingBagRevampUX(StyledKeepShoppingCTA);
const KeepShoppingText = withShoppingBagRevampUX(StyledKeepShoppingText);

export function TextProgress() {
  const cart = useSelector(state => state.checkout);
  const cartTotal = cart.total || 0;
  const memberCreditsApplied = useSelector(getCreditsApplied) || 0;

  const nextCreditValue = useSelector(getNextMemberCredit);

  if (memberCreditsApplied > 0) {
    return (
      <FormattedMessage
        id="global_checkout.available_credits_under_threshold_progress_bar_2"
        defaultMessage="You are {total} away from applying another Member Credit!"
        values={{
          total: <Price amount={(nextCreditValue - cartTotal).toFixed(2)} />,
        }}
      />
    );
  }

  return (
    <FormattedMessage
      id="global_checkout.available_credits_under_threshold_progress_bar"
      defaultMessage="You are {total} away from applying a Member Credit!"
      values={{
        total: <Price amount={(nextCreditValue - cartTotal).toFixed(2)} />,
      }}
    />
  );
}

export const CreditStatusContent = ({
  cartHasPromoConflicts,
  creditStatus,
  statusText,
  shouldShowProgress,
}) => {
  if (cartHasPromoConflicts) {
    return (
      <PromoConflictHeader>
        <FormattedMessage
          id="site_account_rewards.nmp_toggle_applied_credits"
          defaultMessage="Apply Credit(s) Instead of Promo?"
        />
      </PromoConflictHeader>
    );
  }

  return (
    <Text
      data-autotag={statusAutotags[creditStatus]}
      creditStatus={creditStatus}
    >
      {shouldShowProgress ? <TextProgress /> : statusText}
    </Text>
  );
};

const MemberCreditsRemainingAmountStyled = styled.span`
  font-weight: ${props => (props.credits ? 'bold' : 'inherit')};
  color: ${props =>
    props.credits ? props.theme.colors.promo : props.theme.colors.black900};

  color: ${({ theme, isCreditsAppliedLabel }) =>
    isCreditsAppliedLabel
      ? `${theme.colors.lavender700}`
      : `${theme.colors.black}
      ${({ credits, theme }) =>
        credits
          ? theme.subHeader.variants.subHeading4Uppercase.textStyles
          : theme.paragraph.variants.paragraph4Uppercase.textStyles}
    text-transform: unset;

  `};
  ${({ isShoppingBagRevampUX, theme }) =>
    isShoppingBagRevampUX &&
    css`
      ${theme.subHeader.variants.subHeading4.textStyles};
      color: ${theme.colors.gunmetal};
    `}
`;
const MemberCreditsRemainingAmount = withShoppingBagRevampUX(
  MemberCreditsRemainingAmountStyled
);
const MemberCreditsAppliedAmountStyled = styled.span`
  font-weight: ${props => (props.credits ? 'bold' : 'inherit')};
  color: ${props =>
    props.credits ? props.theme.colors.promo : props.theme.colors.black900};

  color: ${({ theme, isCreditsAppliedLabel }) =>
    isCreditsAppliedLabel
      ? `${theme.colors.lavender700}`
      : `${theme.colors.black}
      ${({ credits, theme }) =>
        credits
          ? theme.subHeader.variants.subHeading4Uppercase.textStyles
          : theme.paragraph.variants.paragraph4Uppercase.textStyles}
    text-transform: unset;
  `};
  ${({ isShoppingBagRevampUX, theme }) =>
    isShoppingBagRevampUX &&
    css`
      ${theme.paragraph.variants.paragraph4.textStyles};
      color: ${theme.colors.gunmetal};
    `}
`;
const MemberCreditsAppliedAmount = withShoppingBagRevampUX(
  MemberCreditsAppliedAmountStyled
);

const Spacer = styled.span`
  padding: 0 ${props => props.theme.spacing.tiny}px;

  &::before {
    content: '|';
  }
`;

const HorizontalBorder = styled.div`
  margin-bottom: ${props => props.theme.spacing.tiny}px;
  margin-top: ${props => props.theme.spacing.tiny}px;
  margin-right: -12px;
  margin-left: -12px;

  border-bottom: solid 2px ${props => props.theme.colors.lavender250};
  border-bottom: solid 1px ${({ theme }) => theme.colors.stone};
  ${({ marginTop }) => (marginTop ? `margin-top: ${marginTop}px` : '')};
`;

export const CreditsAmountInfoRevamped = ({
  creditsApplied,
  creditsAvailable,
  creditsRemaining,
}) => {
  if (!creditsApplied) {
    return (
      <MemberCreditsContainer isShoppingBagRevampUX>
        <MemberCreditsRemainingAmount
          isShoppingBagRevampUX
          credits={creditsRemaining}
          data-autotag="member_credits_remaining"
        >
          <FormattedMessage
            id="global_checkout.member_credits_remaining"
            defaultMessage="{creditsRemaining} remaining"
            values={{ creditsRemaining }}
          />
        </MemberCreditsRemainingAmount>
      </MemberCreditsContainer>
    );
  } else {
    return (
      <MemberCreditsContainer isShoppingBagRevampUX>
        <MemberCreditsAppliedAmount
          isShoppingBagRevampUX
          credits={creditsApplied}
          data-autotag="member_credits_remaining"
        >
          <FormattedMessage
            id="global_checkout.member_credits_applied_of_available"
            defaultMessage="{creditsApplied} of {creditsAvailable} applied"
            values={{
              creditsAvailable,
              creditsApplied,
            }}
          />
        </MemberCreditsAppliedAmount>
      </MemberCreditsContainer>
    );
  }
};

export const CreditsAmountInfo = ({
  availableMemberCredits,
  tokenToCashv2Enabled,
  membershipPrice,
  creditsRemaining,
  memberCreditsApplied,
}) => {
  const isShoppingBagRevampUX = useIsShoppingBagRevampUX();
  const cartTotalWithoutConflictingPromos =
    useGetCartTotalWithoutConflictingPromos();
  if (
    !(
      availableMemberCredits > 0 &&
      (tokenToCashv2Enabled ||
        cartTotalWithoutConflictingPromos >= membershipPrice)
    )
  ) {
    return null;
  }

  if (isShoppingBagRevampUX) {
    return (
      <CreditsAmountInfoRevamped
        creditsApplied={memberCreditsApplied}
        creditsRemaining={creditsRemaining}
        creditsAvailable={availableMemberCredits}
      />
    );
  }

  return (
    <MemberCreditsContainer>
      <MemberCreditsRemainingAmount
        credits={creditsRemaining}
        data-autotag="member_credits_remaining"
      >
        <FormattedMessage
          id="global_checkout.member_credits_remaining"
          defaultMessage="{creditsRemaining} remaining"
          values={{ creditsRemaining }}
        />
      </MemberCreditsRemainingAmount>
      {memberCreditsApplied > 0 ? (
        <>
          <Spacer />
          <MemberCreditsAppliedAmount
            credits={memberCreditsApplied}
            data-autotag="member_credits_applied"
            isCreditsAppliedLabel
          >
            <FormattedMessage
              id="global_checkout.member_credits_applied"
              defaultMessage="{creditsApplied} applied"
              values={{ creditsApplied: memberCreditsApplied }}
            />
          </MemberCreditsAppliedAmount>
        </>
      ) : null}
    </MemberCreditsContainer>
  );
};

export const PromoSection = ({
  cartHasPromoConflicts,
  tokenToCashv2Enabled,
  isCreditApplied,
  membershipPrice,
  shouldShowProgress,
}) => {
  const shouldShowDivider = shouldShowProgress && isCreditApplied;
  if (!cartHasPromoConflicts) {
    return (
      <>
        {shouldShowDivider && <HorizontalBorder />}
        <PromoConflictNMP isCreditApplied={isCreditApplied} />
      </>
    );
  }

  if (tokenToCashv2Enabled) {
    return (
      <>
        {shouldShowDivider && <HorizontalBorder />}

        <CreditsCopyContainer>
          <FormattedMessage
            id="global_checkout.available_credits_under_threshold_copy"
            defaultMessage="Available Member Credits will be automatically applied to your order of {price} or more."
            values={{
              price: <Price amount={membershipPrice} />,
            }}
          />
        </CreditsCopyContainer>
      </>
    );
  }

  return null;
};

export const KeepShoppingSection = () => {
  return (
    <KeepShoppingCTA>
      <Link
        data-autotag="nmp_keep_shopping_button"
        route="/features/best-sellers"
      >
        <KeepShoppingText>
          <FormattedMessage
            id="global_checkout.keep_shopping_cta"
            defaultMessage="Keep Shopping"
          />
        </KeepShoppingText>
      </Link>
    </KeepShoppingCTA>
  );
};

export const MemberCreditsLoading = ({ creditStatus }) =>
  creditStatus === CreditStatus.applying ||
  creditStatus === CreditStatus.removing ? (
    <Loading />
  ) : null;
